import React, { useCallback, useEffect, useRef, useState } from "react";
import Breadcrump from "../../components/Breadcrump";
import wishlistAPI from "../../api/wishlistApi";
import Product from "../products/Card";

const Wishlist = () => {
  const [products, setProducts] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState(null);

  const pageRef = useRef(0); // last page requested
  const fetchingRef = useRef(false); // sync guard: prevents duplicate fetches while scrolling
  const hasMoreRef = useRef(true);
  const sentinelRef = useRef(null);

  const fetchNext = useCallback(async () => {
    if (fetchingRef.current || !hasMoreRef.current) return;
    fetchingRef.current = true;
    setLoading(true);

    const pageNum = pageRef.current + 1;

    try {
      const res = await wishlistAPI.listing({ page: pageNum });

      if (res.code === 200) {
        const { products: items = [], pagination } = res.data || {};
        pageRef.current = pageNum;

        setProducts((prev) => {
          const base = pageNum === 1 ? [] : prev;
          const seen = new Set(base.map((p) => p._id));
          return [...base, ...items.filter((p) => !seen.has(p._id))];
        });

        const total = pagination?.totalCount || 0;
        setTotalCount(total);

        const more = items.length > 0 && pageNum * 6 < total;
        hasMoreRef.current = more;
        setHasMore(more);
      } else {
        hasMoreRef.current = false;
        setHasMore(false);
        if (pageNum === 1) setError("No Product found in wishlist.");
      }
    } catch (err) {
      hasMoreRef.current = false;
      setHasMore(false);
      if (pageNum === 1) setError("You have to log in to view your wishlist.");
    } finally {
      fetchingRef.current = false;
      setLoading(false);
    }
  }, []);

  // first page
  useEffect(() => {
    fetchNext();
  }, [fetchNext]);

  // infinite scroll via IntersectionObserver (no scroll-event spam => no flicker)
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && pageRef.current > 0) fetchNext();
      },
      { rootMargin: "300px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [fetchNext, products.length]);

  return (
    <>
      <Breadcrump title="WishList" />

      <section className="s-contact flat-spacing-13 pb-5">
        <div className="container">
          {error && <p className="text-danger text-center">{error}</p>}

          {products && products.length > 0 ? (
            <>
              <div className="blog-count text-left mb-4">
                Showing <b>{products?.length}</b> of <b>{totalCount}</b>{" "}
                products
              </div>

              <div className="row g-3 mt-2">
                {products.map((product, index) => (
                  <div key={product._id || index} className="col-md-3 col-6">
                    <Product item={product} />
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              {!error && (
                <p className="text-center">No product found in wishlist</p>
              )}
            </>
          )}

          <div className="row">
            {loading &&
              [...Array(3)].map((_, i) => (
                <div className="col-md-4" key={i}>
                  <div className="blog-item-v2 mb-3">
                    <div className="entry-image hover-img skeleton-box skeleton-image" />

                    <div className="entry-content">
                      <div className="info-box">
                        <ul className="meta-list">
                          <li className="item skeleton-box skeleton-meta" />
                          <li className="item skeleton-box skeleton-meta" />
                        </ul>
                        <div className="title skeleton-box skeleton-title" />
                        <div className="desc skeleton-box skeleton-desc" />
                      </div>
                      <div className="btn-readmore skeleton-box skeleton-btn" />
                    </div>
                  </div>
                </div>
              ))}
          </div>

          <div ref={sentinelRef} style={{ height: 1 }} />

          {!hasMore && !loading && products.length > 6 && (
            <p className="text-center mt-4">You've reached the end.</p>
          )}
        </div>
      </section>
    </>
  );
};

export default Wishlist;
